import { forwardRef, memo, useCallback, useMemo, type Ref } from 'react';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { TypographyVariant } from '@mui/material/styles';
import Box from '@mui/material/Box';
// Skillmore UI Components
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
// local imports
import { AdminEmployee, AdminJob } from '../graphql/types';
import { TalentEmployeeObject } from '../graphql/customTypes';
import { Job } from '../models/job';
import { CareerPath } from '../models/careerPath';
import { PATH_JOB } from '../config/paths';

type RoleNameProps = {
  readonly isEmployee?: boolean;
  readonly code?: string | null;
  readonly title: string;
  readonly isCurrent?: boolean | null;
  readonly isTarget?: boolean | null;
  readonly withoutLink?: boolean;
  readonly variant?: TypographyVariant | 'inherit';
  readonly bold?: boolean;
  readonly onClick?: ((code: string, job?: Job | AdminJob, context?: TalentEmployeeObject | AdminEmployee) => void) | null;
  readonly role?: Job | CareerPath | AdminJob;
  readonly context?: TalentEmployeeObject | AdminEmployee;
  readonly className?: string;
}

const RoleName = forwardRef<HTMLAnchorElement | HTMLDivElement, RoleNameProps>(({
  isEmployee = false,
  code,
  title,
  isCurrent = false,
  isTarget = false,
  withoutLink = false,
  variant,
  bold,
  onClick,
  role,
  context,
  className
}, ref) => {
  const link = useMemo(() => withoutLink || !code || onClick ? undefined
    : injectParams(PATH_JOB, { role_id: code }), [withoutLink, code, onClick]);

  const handleClick = useCallback(() => {
    if (code && onClick) onClick(code, role as Job | AdminJob, context);
  }, [code, role, context, onClick]);

  return isCurrent || isTarget ? (
    <Box ref={ref as Ref<HTMLDivElement>} display="flex" alignItems="center" flexWrap="wrap">
      <Box pr={2} color={code ? undefined : 'secondary.main'} className={className}>
        {withoutLink ? title : (
          <StandardLink variant={variant} bold={bold} to={link} onClick={code && onClick ? handleClick : undefined}>
            {title}
          </StandardLink>
        )}
      </Box>
      <BoxTypography color="info.light" fontStyle="italic" variant="body2">
        <FormattedMessage
            id={isCurrent
              ? (isEmployee && 'common.current_role') || 'common.current_role_supv'
              : 'common.target_role'}
        />
      </BoxTypography>
    </Box>
  ) : (withoutLink && (
    <Box ref={ref as Ref<HTMLDivElement>} color="secondary.main" className={className}>
      {title}
    </Box>
  )) || (
    <StandardLink
        ref={ref as Ref<HTMLAnchorElement>}
        variant={variant}
        bold={bold}
        to={link}
        onClick={code && onClick ? handleClick : undefined}
    >
      {title}
    </StandardLink>
  );
});

RoleName.displayName = 'RoleName';

export default memo(RoleName);
